/**
 * AI-чат — глобальная боковая панель с историей бесед.
 *
 * - Доступен из любого места приложения (через AiPanelContext)
 * - Ресайз за левый край
 * - Markdown-рендер ответов (react-markdown)
 * - Свободный ввод + пресеты как подсказки
 * - Переключатель «Режим действий» (enableTools)
 * - Отображение tool_call / tool_result в потоке
 * - Список бесед (история) с возможностью продолжить, удалить, переименовать
 * - Автосохранение сообщений на сервере
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Drawer, Input, Space, Tag, Typography, Empty, Tooltip, Switch, List, Popconfirm, Select, message as messageApi } from 'antd';
import {
  SendOutlined,
  CloseOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  PlusOutlined,
  HistoryOutlined,
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  MessageOutlined,
  CopyOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { AiSparkIcon } from '../../shared/components/AiSparkIcon.tsx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { AiChatRequest, AiPreset, AiConversationSummary } from '@lens/shared';
import {
  streamChat,
  listConversations,
  getConversation,
  deleteConversation,
  renameConversation,
  fetchAiModels,
  type SseCallbacks,
} from './api.ts';
import { useAiPanel } from './AiPanelContext.tsx';
import './ai-markdown.css';

const { Text } = Typography;
const { TextArea } = Input;

interface ToolCallInfo {
  toolCallId: string;
  name: string;
  args: Record<string, unknown>;
  /** P2.3 — последний прогресс-тик, если тул его шлёт. */
  progress?: { message: string; current: number; total: number };
}

interface ToolResultInfo {
  toolCallId: string;
  name: string;
  success: boolean;
  summary: string;
}

type MessagePart =
  | { type: 'text'; content: string }
  | { type: 'tool_call'; info: ToolCallInfo }
  | { type: 'tool_result'; info: ToolResultInfo };

interface Message {
  role: 'user' | 'assistant';
  parts: MessagePart[];
}

type PanelView = 'chat' | 'history';

const PRESET_EXAMPLES: Array<{ label: string; preset?: AiPreset; message: string }> = [
  { label: '📊 Саммари квартала', preset: 'summary_quarter', message: 'Сделай саммари по текущему кварталу' },
  { label: '📋 Анализ бэклога', preset: 'analysis_backlog', message: 'Какие цели из бэклога стоит взять в следующий квартал?' },
  { label: '📝 Отчёт для руководства', preset: 'quarterly_report', message: 'Сгенерируй отчёт по результатам квартала' },
  { label: '⚠️ Проблемные записи', message: 'Какие записи не имеют ответственных или критериев готовности?' },
];

const TOOL_NAMES_RU: Record<string, string> = {
  update_items: 'Обновление записей',
  create_item: 'Создание записи',
  delete_items: 'Удаление записей',
  create_view: 'Создание представления',
  update_view: 'Обновление представления',
  delete_view: 'Удаление представления',
  add_comment: 'Добавление комментария',
  add_member: 'Добавление участника',
  update_member_role: 'Изменение роли',
  remove_member: 'Удаление участника',
  create_space: 'Создание пространства',
  update_space: 'Обновление пространства',
  archive_space: 'Архивация пространства',
  create_item_type: 'Создание типа записи',
  create_field: 'Создание поля',
  update_field: 'Обновление поля',
  create_dictionary: 'Создание справочника',
  create_dictionary_entry: 'Добавление в справочник',
  update_dictionary_entry: 'Обновление записи справочника',
  delete_dictionary_entry: 'Удаление из справочника',
  list_spaces: 'Загрузка пространств',
  list_views: 'Загрузка представлений',
  list_members: 'Загрузка участников',
  list_users: 'Загрузка пользователей',
  get_item: 'Загрузка записи',
  query_items: 'Поиск записей',
  list_item_types: 'Загрузка типов записей',
  list_dictionaries: 'Загрузка справочников',
  list_comments: 'Загрузка комментариев',
  get_audit_log: 'Загрузка истории изменений',
};

const MIN_WIDTH = 380;
const MAX_WIDTH = 900;
const DEFAULT_WIDTH = 520;

export function AiChatPanel(): JSX.Element {
  const { open, scope, closePanel } = useAiPanel();
  const viewId = scope.viewId;
  const spaceName = scope.spaceName;
  const viewName = scope.viewName;

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [contextSize, setContextSize] = useState<number | null>(null);
  const [decompositionSteps, setDecompositionSteps] = useState<number | null>(null);
  const [drawerWidth, setDrawerWidth] = useState(DEFAULT_WIDTH);
  const [enableTools, setEnableTools] = useState(false);
  const [model, setModel] = useState<string | undefined>();
  const [models, setModels] = useState<string[]>([]);
  const [defaultModel, setDefaultModel] = useState<string | null>(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [panelView, setPanelView] = useState<PanelView>('chat');
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [conversations, setConversations] = useState<AiConversationSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const stickyBottomRef = useRef(true);
  const lastUserMessageRef = useRef<{ text: string; preset?: AiPreset } | null>(null);

  // Автоскролл: прилипаем к низу, только если пользователь не прокрутил вверх
  useEffect(() => {
    if (!stickyBottomRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const threshold = 40;
    stickyBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  }, []);

  // Ресайз мышью за левый край
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const delta = dragRef.current.startX - e.clientX;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragRef.current.startWidth + delta));
      setDrawerWidth(newWidth);
    };
    const handleMouseUp = () => {
      dragRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const startDrag = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: drawerWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [drawerWidth]);

  // Сдвигаем контент на ширину Lensy-панели, чтобы она не перекрывала рабочую область
  useEffect(() => {
    const body = document.body;
    if (open) {
      body.classList.add('lensy-panel-open');
    } else {
      body.classList.remove('lensy-panel-open');
    }
    return () => {
      body.classList.remove('lensy-panel-open');
    };
  }, [open, drawerWidth]);

  /** Загрузить список моделей LLM (лениво — при первом открытии селектора). */
  const loadModels = useCallback(async () => {
    if (modelsLoaded || modelsLoading) return;
    setModelsLoading(true);
    try {
      const data = await fetchAiModels();
      setModels(data.models);
      setDefaultModel(data.current);
    } catch {
      // ignore — оставим пусто, пользователь увидит «нет моделей» в Select
    } finally {
      setModelsLoaded(true);
      setModelsLoading(false);
    }
  }, [modelsLoaded, modelsLoading]);

  // При первом открытии панели подгружаем модели, чтобы селектор сразу показал текущую.
  useEffect(() => {
    if (open && !modelsLoaded && !modelsLoading) {
      void loadModels();
    }
  }, [open, modelsLoaded, modelsLoading, loadModels]);

  /** Загрузить список бесед */
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await listConversations();
      setConversations(data.conversations);
    } catch {
      // ignore
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  /** Открыть экран истории */
  const showHistory = useCallback(() => {
    setPanelView('history');
    void loadHistory();
  }, [loadHistory]);

  /** Загрузить беседу и показать её */
  const openConversation = useCallback(async (convId: string) => {
    try {
      const conv = await getConversation(convId);
      setConversationId(conv.id);
      setMessages(
        conv.messages.map((m) => ({
          role: m.role as 'user' | 'assistant',
          parts: m.parts as MessagePart[],
        })),
      );
      setContextSize(null);
      setDecompositionSteps(null);
      setPanelView('chat');
    } catch {
      // ignore
    }
  }, []);

  /** Удалить беседу */
  const handleDeleteConversation = useCallback(async (convId: string) => {
    try {
      await deleteConversation(convId);
      setConversations((prev) => prev.filter((c) => c.id !== convId));
      if (conversationId === convId) {
        setConversationId(undefined);
        setMessages([]);
      }
    } catch {
      // ignore
    }
  }, [conversationId]);

  /** Переименовать беседу */
  const handleRename = useCallback(async (convId: string, title: string) => {
    try {
      await renameConversation(convId, title);
      setConversations((prev) =>
        prev.map((c) => (c.id === convId ? { ...c, title } : c)),
      );
    } catch {
      // ignore
    }
    setEditingTitle(null);
  }, []);

  /** Добавить part к последнему assistant-сообщению. */
  const appendToAssistant = useCallback((part: MessagePart) => {
    setMessages(prev => {
      const copy = [...prev];
      const last = copy[copy.length - 1];
      if (last?.role === 'assistant') {
        const lastPart = last.parts[last.parts.length - 1];
        if (part.type === 'text' && lastPart?.type === 'text') {
          copy[copy.length - 1] = {
            ...last,
            parts: [...last.parts.slice(0, -1), { type: 'text', content: lastPart.content + part.content }],
          };
        } else {
          copy[copy.length - 1] = { ...last, parts: [...last.parts, part] };
        }
      }
      return copy;
    });
  }, []);

  /**
   * Обновить прогресс tool_call-параметра по toolCallId (P2.3).
   * Ищем последний tool_call с нужным ID (LLM может перезапросить тул
   * с тем же ID — в таком случае обновляем самый свежий) и прикладываем
   * к нему progress. Если not found — игнорируем (tool_result уже пришёл).
   */
  const updateToolProgress = useCallback(
    (toolCallId: string, message: string, current: number, total: number) => {
      setMessages(prev => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last?.role !== 'assistant') return prev;
        const parts = [...last.parts];
        for (let i = parts.length - 1; i >= 0; i--) {
          const p = parts[i];
          if (p?.type === 'tool_call' && p.info.toolCallId === toolCallId) {
            parts[i] = {
              ...p,
              info: { ...p.info, progress: { message, current, total } },
            };
            copy[copy.length - 1] = { ...last, parts };
            return copy;
          }
        }
        return prev;
      });
    },
    [],
  );

  const sendMessage = useCallback(async (text: string, preset?: AiPreset) => {
    if (!text.trim() || streaming) return;
    lastUserMessageRef.current = { text: text.trim(), preset };
    stickyBottomRef.current = true;

    // Собираем историю из предыдущих сообщений (только текстовые части)
    // Берём не более 50 последних сообщений (лимит схемы), каждое — до 30k символов
    const MAX_HISTORY_CONTENT = 30000;
    const MAX_HISTORY_MESSAGES = 50;
    const history = messages
      .slice(-MAX_HISTORY_MESSAGES)
      .map((msg) => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.parts
          .filter((p) => p.type === 'text')
          .map((p) => (p as { type: 'text'; content: string }).content)
          .join('')
          .slice(0, MAX_HISTORY_CONTENT),
      }))
      .filter((h) => h.content.length > 0);

    const userMsg: Message = { role: 'user', parts: [{ type: 'text', content: text.trim() }] };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setStreaming(true);
    setDecompositionSteps(null);

    // Пустое assistant-сообщение для стриминга
    setMessages(prev => [...prev, { role: 'assistant', parts: [] }]);

    const controller = new AbortController();
    abortRef.current = controller;

    const request: AiChatRequest = {
      scope: viewId ? { kind: 'view' as const, viewId } : { kind: 'general' as const },
      message: text.trim(),
      ...(preset ? { preset } : {}),
      ...(enableTools ? { enableTools: true } : {}),
      ...(history.length > 0 ? { history } : {}),
      ...(conversationId ? { conversationId } : {}),
      ...(model ? { model } : {}),
    };

    const callbacks: SseCallbacks = {
      onMeta: (meta) => {
        setContextSize(meta.contextSize);
        if (meta.decompositionApplied) {
          setDecompositionSteps(meta.decompositionSteps ?? 0);
        } else {
          setDecompositionSteps(null);
        }
      },
      onDelta: (chunk) => {
        appendToAssistant({ type: 'text', content: chunk });
      },
      onToolCall: (toolCallId, name, args) => {
        appendToAssistant({ type: 'tool_call', info: { toolCallId, name, args } });
      },
      onToolProgress: (toolCallId, _name, message, current, total) => {
        updateToolProgress(toolCallId, message, current, total);
      },
      onToolResult: (toolCallId, name, success, summary) => {
        appendToAssistant({ type: 'tool_result', info: { toolCallId, name, success, summary } });
      },
      onDone: (_usage, convId) => {
        if (convId) setConversationId(convId);
        setStreaming(false);
        abortRef.current = null;
      },
      onError: (code, message) => {
        appendToAssistant({ type: 'text', content: `**Ошибка:** ${message ?? code}` });
        setStreaming(false);
        abortRef.current = null;
      },
    };

    try {
      await streamChat(request, callbacks, controller.signal);
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        appendToAssistant({ type: 'text', content: '**Ошибка:** Не удалось получить ответ от Lensy' });
      }
      setStreaming(false);
      abortRef.current = null;
    }
  }, [viewId, streaming, enableTools, model, appendToAssistant, updateToolProgress, messages, conversationId]);

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    setStreaming(false);
  }, []);

  const handleNewChat = useCallback(() => {
    if (streaming) {
      abortRef.current?.abort();
      setStreaming(false);
    }
    setMessages([]);
    setContextSize(null);
    setDecompositionSteps(null);
    setConversationId(undefined);
    setPanelView('chat');
    lastUserMessageRef.current = null;
  }, [streaming]);

  const handleRegenerate = useCallback(() => {
    const last = lastUserMessageRef.current;
    if (!last || streaming) return;
    // Удаляем последнюю пару user→assistant и перезапрашиваем
    setMessages((prev) => {
      const copy = [...prev];
      // убираем последний assistant (если есть)
      if (copy[copy.length - 1]?.role === 'assistant') copy.pop();
      // убираем последний user
      if (copy[copy.length - 1]?.role === 'user') copy.pop();
      return copy;
    });
    // Отложим send на следующий тик, чтобы setMessages успел применить
    setTimeout(() => void sendMessage(last.text, last.preset), 0);
  }, [streaming, sendMessage]);

  const handleCopyMessage = useCallback((text: string) => {
    if (!text) return;
    void navigator.clipboard.writeText(text).then(
      () => { void messageApi.success('Скопировано', 1); },
      () => { void messageApi.error('Не удалось скопировать'); },
    );
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  };

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            {panelView === 'history' && (
              <Button type="text" size="small" icon={<ArrowLeftOutlined />} onClick={() => setPanelView('chat')} />
            )}
            {panelView === 'chat' && (
              <AiSparkIcon
                style={{ fontSize: 18 }}
                className={streaming ? 'lensy-icon-pulse' : ''}
              />
            )}
            <span>{panelView === 'history' ? 'История чатов' : 'Lensy'}</span>
            {panelView === 'chat' && streaming && (
              <Tag color="processing" style={{ marginLeft: 4 }}>
                <LoadingOutlined spin style={{ marginRight: 4 }} />
                обрабатывает
              </Tag>
            )}
            {panelView === 'chat' && contextSize !== null && (
              <Tag color="blue">{contextSize} записей</Tag>
            )}
            {panelView === 'chat' && decompositionSteps !== null && (
              <Tag color="gold">
                {`Декомпозиция: ${decompositionSteps} ${pluralizeSteps(decompositionSteps)}`}
              </Tag>
            )}
          </Space>
          <Space size={4}>
            {panelView === 'chat' && (
              <>
                <Tooltip title="История чатов">
                  <Button type="text" size="small" icon={<HistoryOutlined />} onClick={showHistory} />
                </Tooltip>
                <Tooltip title="Новый чат">
                  <Button type="text" size="small" icon={<PlusOutlined />} onClick={handleNewChat} />
                </Tooltip>
              </>
            )}
          </Space>
        </div>
      }
      open={open}
      onClose={closePanel}
      width={drawerWidth}
      mask={false}
      maskClosable={false}
      keyboard={false}
      rootClassName="lensy-drawer"
      styles={{ body: { display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' } }}
    >
      {/* Полоса ресайза — левый край */}
      <div
        onMouseDown={startDrag}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 6,
          cursor: 'col-resize',
          zIndex: 10,
        }}
        title="Потяните для изменения ширины"
      />

      {panelView === 'history' ? (
        /* ─── Экран истории бесед ─── */
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={handleNewChat}
            style={{ marginBottom: 16 }}
          >
            Новый чат
          </Button>

          <List
            loading={historyLoading}
            dataSource={conversations}
            locale={{ emptyText: 'Нет сохранённых бесед' }}
            renderItem={(conv) => (
              <List.Item
                style={{ cursor: 'pointer', padding: '8px 12px', borderRadius: 8 }}
                actions={[
                  <Tooltip key="edit" title="Переименовать">
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingTitle(conv.id);
                        setEditTitleValue(conv.title);
                      }}
                    />
                  </Tooltip>,
                  <Popconfirm
                    key="del"
                    title="Удалить беседу?"
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      void handleDeleteConversation(conv.id);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                    okText="Удалить"
                    cancelText="Отмена"
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>,
                ]}
                onClick={() => void openConversation(conv.id)}
              >
                <List.Item.Meta
                  avatar={<MessageOutlined style={{ fontSize: 18, color: '#1677ff', marginTop: 4 }} />}
                  title={
                    editingTitle === conv.id ? (
                      <Input
                        size="small"
                        value={editTitleValue}
                        onChange={(e) => setEditTitleValue(e.target.value)}
                        onPressEnter={() => void handleRename(conv.id, editTitleValue)}
                        onBlur={() => void handleRename(conv.id, editTitleValue)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    ) : (
                      <Text ellipsis style={{ maxWidth: 260 }}>{conv.title}</Text>
                    )
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {conv.messageCount} сообщ. · {formatRelativeDate(conv.updatedAt)}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        </div>
      ) : (
        /* ─── Экран чата ─── */
        <>
          {/* Контекст + переключатель действий */}
          <div style={{ padding: '8px 16px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {(spaceName || viewName) ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Контекст: {spaceName} → {viewName}
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {viewId ? 'Контекст привязан' : 'Без контекста представления'}
                </Text>
              )}
              <Space size={8}>
                <Tooltip title={defaultModel ? `Модель LLM. По умолчанию: ${defaultModel}` : 'Модель LLM'}>
                  <Select
                    size="small"
                    value={model ?? defaultModel ?? undefined}
                    placeholder="Модель"
                    loading={modelsLoading}
                    onDropdownVisibleChange={(o) => { if (o) void loadModels(); }}
                    onChange={(val) => setModel(val)}
                    options={(models.length > 0 ? models : (defaultModel ? [defaultModel] : []))
                      .map((m) => ({ label: m, value: m }))}
                    style={{ minWidth: 140, maxWidth: 220 }}
                    data-testid="ai-chat-model-select"
                    showSearch
                    optionFilterProp="label"
                  />
                </Tooltip>
                <Tooltip title="Разрешить Lensy выполнять действия: создавать, обновлять и удалять записи">
                  <Space size={4}>
                    <ThunderboltOutlined style={{ color: enableTools ? '#faad14' : '#d9d9d9', fontSize: 14 }} />
                    <Switch
                      size="small"
                      checked={enableTools}
                      onChange={setEnableTools}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>Действия</Text>
                  </Space>
                </Tooltip>
              </Space>
            </div>
          </div>

          {/* Сообщения */}
          <div
            ref={messagesContainerRef}
            onScroll={handleMessagesScroll}
            style={{ flex: 1, overflow: 'auto', padding: 16 }}
          >
            {messages.length === 0 && (
              <div style={{ paddingTop: 24 }}>
                <Empty
                  description={viewId ? 'Задайте вопрос по данным представления' : 'Задайте вопрос Lensy'}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
                <div style={{ marginTop: 20 }}>
                  <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
                    {viewId ? 'Или выберите готовый запрос:' : 'Попробуйте начать с примера:'}
                  </Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(viewId
                      ? PRESET_EXAMPLES
                      : PRESET_EXAMPLES.filter((ex) => !ex.preset)
                    ).map((ex) => (
                      <Button
                        key={ex.label}
                        size="small"
                        onClick={() => void sendMessage(ex.message, ex.preset)}
                        style={{ height: 'auto', whiteSpace: 'normal', padding: '4px 12px' }}
                      >
                        {ex.label}
                      </Button>
                    ))}
                  </div>
                </div>
                {enableTools && (
                  <div style={{ marginTop: 16, padding: '8px 12px', background: '#fffbe6', borderRadius: 8, border: '1px solid #ffe58f' }}>
                    <Text style={{ fontSize: 12 }}>
                      <ThunderboltOutlined style={{ color: '#faad14', marginRight: 4 }} />
                      <strong>Режим действий включён.</strong> Lensy может управлять записями, представлениями, участниками и комментариями.
                    </Text>
                  </div>
                )}
              </div>
            )}

            {streaming && messages.length > 0 && messages[messages.length - 1]?.role === 'user' && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 12px',
                  marginBottom: 12,
                  color: 'var(--color-text-secondary)',
                  fontSize: 13,
                }}
              >
                <span className="lensy-thinking-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
                <span>Lensy думает…</span>
              </div>
            )}

            {messages.map((msg, i) => {
              const isLastMessage = i === messages.length - 1;
              const isLastAssistantDone = msg.role === 'assistant' && isLastMessage && !streaming && msg.parts.length > 0;
              const assistantText = msg.role === 'assistant' ? getTextContent(msg.parts) : '';
              return (
                <div
                  key={i}
                  style={{
                    marginBottom: 16,
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <div
                    style={{
                      maxWidth: '90%',
                      wordBreak: 'break-word',
                      fontSize: 14,
                      lineHeight: 1.6,
                    }}
                  >
                    {msg.role === 'user' ? (
                      <div style={{
                        padding: '8px 14px',
                        borderRadius: '16px 16px 4px 16px',
                        background: '#e6f4ff',
                        whiteSpace: 'pre-wrap',
                      }}>
                        {getTextContent(msg.parts)}
                      </div>
                    ) : (
                      <div>
                        {msg.parts.map((part, j) => renderPart(part, j, streaming && isLastMessage && j === msg.parts.length - 1))}
                        {streaming && isLastMessage && msg.parts.length === 0 && <LoadingOutlined />}
                        {isLastAssistantDone && (
                          <div style={{ display: 'flex', gap: 4, marginTop: 4, marginLeft: 4 }}>
                            {assistantText && (
                              <Tooltip title="Скопировать ответ">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<CopyOutlined />}
                                  onClick={() => handleCopyMessage(assistantText)}
                                />
                              </Tooltip>
                            )}
                            {lastUserMessageRef.current && (
                              <Tooltip title="Сгенерировать заново">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<ReloadOutlined />}
                                  onClick={handleRegenerate}
                                />
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Поле ввода */}
          <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <TextArea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={enableTools ? 'Опишите действие или задайте вопрос...' : 'Задайте вопрос по данным...'}
                autoSize={{ minRows: 1, maxRows: 6 }}
                disabled={streaming}
                style={{ resize: 'none', flex: 1, borderRadius: 8 }}
              />
              {streaming ? (
                <Button
                  icon={<CloseOutlined />}
                  onClick={handleStop}
                  danger
                  style={{ flexShrink: 0, borderRadius: 8 }}
                  title="Остановить генерацию"
                />
              ) : (
                <Button
                  icon={<SendOutlined />}
                  onClick={() => void sendMessage(input)}
                  type="primary"
                  disabled={!input.trim()}
                  style={{ flexShrink: 0, borderRadius: 8 }}
                  title="Отправить (Enter)"
                />
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Enter — отправить, Shift+Enter — перенос строки
              </Text>
              {input.length > 4000 && (
                <Text type="warning" style={{ fontSize: 11 }}>
                  {input.length.toLocaleString('ru-RU')} симв. Рекомендуется декомпозировать запрос на шаги.
                </Text>
              )}
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
}

// ─── Helper renderers ─────────────────────────────────────────────────

function getTextContent(parts: MessagePart[]): string {
  return parts.filter((p) => p.type === 'text').map((p) => (p as { type: 'text'; content: string }).content).join('');
}

function renderPart(part: MessagePart, index: number, isLast: boolean): JSX.Element | null {
  if (part.type === 'text') {
    return (
      <div
        key={index}
        style={{
          padding: '12px 16px',
          borderRadius: '16px 16px 16px 4px',
          background: '#f5f5f5',
        }}
      >
        <div className="ai-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{part.content}</ReactMarkdown>
          {isLast && <LoadingOutlined style={{ marginLeft: 4 }} />}
        </div>
      </div>
    );
  }

  if (part.type === 'tool_call') {
    const { name, args, progress } = part.info;
    const toolLabel = TOOL_NAMES_RU[name] || name;
    const progressPct = progress && progress.total > 0
      ? Math.min(100, Math.round((progress.current / progress.total) * 100))
      : null;
    return (
      <div
        key={index}
        style={{
          margin: '8px 0',
          padding: '8px 12px',
          borderRadius: 8,
          background: '#f0f5ff',
          border: '1px solid #adc6ff',
          fontSize: 13,
        }}
      >
        <Space size={6}>
          <ToolOutlined style={{ color: '#1677ff' }} />
          <Text strong style={{ fontSize: 13 }}>{toolLabel}</Text>
        </Space>
        {args.reason != null && (
          <div style={{ marginTop: 4, color: '#595959' }}>
            {String(args.reason)}
          </div>
        )}
        {Array.isArray(args.item_ids) && (
          <div style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {String((args.item_ids as string[]).length)} {pluralizeRecords((args.item_ids as string[]).length)}
            </Text>
          </div>
        )}
        {args.fields != null && typeof args.fields === 'object' && (
          <div style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {'Поля: ' + Object.keys(args.fields as Record<string, unknown>).join(', ')}
            </Text>
          </div>
        )}
        {progress && (
          <div style={{ marginTop: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#595959' }}>
              <LoadingOutlined />
              <span>{progress.message}</span>
              {progressPct !== null && <span style={{ marginLeft: 'auto', fontWeight: 500 }}>{progressPct}%</span>}
            </div>
            {progress.total > 0 && (
              <div
                style={{
                  marginTop: 4,
                  height: 4,
                  background: '#e6e6e6',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${progressPct ?? 0}%`,
                    background: '#1677ff',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (part.type === 'tool_result') {
    const { success, summary } = part.info;
    return (
      <div
        key={index}
        style={{
          margin: '4px 0 8px',
          padding: '6px 12px',
          borderRadius: 8,
          background: success ? '#f6ffed' : '#fff2f0',
          border: `1px solid ${success ? '#b7eb8f' : '#ffa39e'}`,
          fontSize: 13,
        }}
      >
        <Space size={6}>
          {success ? (
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
          )}
          <Text style={{ fontSize: 13 }}>{summary}</Text>
        </Space>
      </div>
    );
  }

  return null;
}

function pluralizeRecords(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'запись';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'записи';
  return 'записей';
}

function pluralizeSteps(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'шаг';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'шага';
  return 'шагов';
}

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин. назад`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} ч. назад`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD} дн. назад`;
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}
